import React from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  Box,
  Typography,
  CircularProgress,
  TextField
} from "@mui/material";
import { FaCheckCircle, FaTimesCircle, FaExclamationTriangle } from "react-icons/fa";
import { useState } from "react";

const QuickActionConfirmModal = ({
  isOpen,
  onClose,
  action,
  vendorName,
  onConfirm,
  loading = false
}) => {
  const [remarks, setRemarks] = useState('');

  if (!action) return null;

  const isApprove = action === 'APPROVE';
  const isReject = action === 'REJECT';
  const isRequestEdit = action === 'REQUEST_EDIT';

  const getDialogContent = () => {
    if (isApprove) {
      return {
        icon: <FaCheckCircle size={48} color="#4caf50" />,
        title: "Approve Vendor",
        message: `Are you sure you want to approve "${vendorName || 'this vendor'}"?`,
        subMessage: "This action will make the vendor live and visible to customers.",
        confirmColor: "success",
        confirmText: "Approve",
        iconBgColor: "rgba(76, 175, 80, 0.1)"
      };
    } else if (isReject) {
      return {
        icon: <FaTimesCircle size={48} color="#f44336" />,
        title: "Reject Vendor",
        message: `Are you sure you want to reject "${vendorName || 'this vendor'}"?`,
        subMessage: "This action cannot be undone. The vendor will need to resubmit.",
        confirmColor: "error",
        confirmText: "Reject",
        iconBgColor: "rgba(244, 67, 54, 0.1)"
      };
    }
    return {
      icon: <FaExclamationTriangle size={48} color="#ff9800" />,
      title: "Confirm Action",
      message: `Are you sure you want to ${action?.toLowerCase()} "${vendorName || 'this vendor'}"?`,
      subMessage: "Please confirm your action.",
      confirmColor: "primary",
      confirmText: "Confirm",
      iconBgColor: "rgba(255, 152, 0, 0.1)"
    };
  };

  const content = getDialogContent();

  return (
    <Dialog
      open={isOpen}
      onClose={loading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          textAlign: 'center'
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mb: 2,
            mt: 1
          }}
        >
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              backgroundColor: content.iconBgColor,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {content.icon}
          </Box>
        </Box>
        <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
          {content.title}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <DialogContentText sx={{ color: 'text.primary', mb: 1 }}>
          {content.message}
        </DialogContentText>
        <Typography variant="body2" color="textSecondary">
          {content.subMessage}
        </Typography>

        <Box sx={{ mt: 2 }}>
          <TextField
            autoFocus
            margin="dense"
            id="remarks"
            label="Remarks (Optional)"
            type="text"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder="Enter any remarks or reason..."
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'center', pb: 3, px: 3, gap: 2 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          disabled={loading}
          size="medium"
          sx={{ minWidth: 100 }}
        >
          Cancel
        </Button>
        <Button
          onClick={() => onConfirm(remarks)}
          variant="contained"
          color={content.confirmColor}
          disabled={loading}
          size="medium"
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{
            minWidth: 100,
            bgcolor: isApprove ? '#4caf50' : isReject ? '#f44336' : isRequestEdit ? '#2196f3' : '#ee448b',
            '&:hover': {
              bgcolor: isApprove ? '#43a047' : isReject ? '#e53935' : isRequestEdit ? '#1976d2' : '#d63d7d'
            }
          }}
        >
          {loading ? 'Processing...' : content.confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuickActionConfirmModal;
